import { useEffect, useRef, useState } from "react";
import { readMarketData, setChange } from "../exports/FormatData";
import { StockPrice } from "../exports/StockPrice";
import { IndexPrice } from "../exports/IndexPrice";
import { GetStockConfig } from "../exports/GetStockConfig";

const LIVEFEED_BASE_URL = process.env.REACT_APP_LIVEFEED_BASE_URL;

/* =========================
   Market timing (IST)
========================= */
function isMarketOpen() {
  const now = new Date();
  const day = now.getDay();
  if (day === 0 || day === 6) return false;

  const minutes =
    now.getHours() * 60 + now.getMinutes();

  return minutes >= 540 && minutes <= 930;
}

/* =========================
   Hook
========================= */
const useMarketStock = (symbol, is_index = false) => {
  const wsRef = useRef(null);
  const heartbeatRef = useRef(null);
  const reconnectRef = useRef(null);

  const configRef = useRef(null);
  const closeRef = useRef([]);
  const stockDataRef = useRef(null);
  const lastTickRef = useRef(null);

  const reconnectAttempt = useRef(0);

  const [stockData, setStockData] = useState(null);

  /* =========================
     Cleanup everything
  ========================= */
  const cleanup = () => {
    clearInterval(heartbeatRef.current);
    clearTimeout(reconnectRef.current);

    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch {}
    }

    wsRef.current = null;
    heartbeatRef.current = null;
    reconnectRef.current = null;
    reconnectAttempt.current = 0;
  };

  /* =========================
     Fetch config + close prices
  ========================= */
  const loadStaticData = async () => {
    if (!symbol) return false;

    try {
      const config = await GetStockConfig(symbol);
      configRef.current = config;

      const closeData = is_index
        ? await IndexPrice(symbol)
        : await StockPrice(config.exchange, config.code);

      if (!closeData?.error) {
        closeRef.current = closeData.closearray || [];
      }

      return true;
    } catch {
      return false;
    }
  };

  /* =========================
     Connect WebSocket
  ========================= */
  const connect = () => {
    if (!configRef.current || wsRef.current) return;

    const ws = new WebSocket(LIVEFEED_BASE_URL);
    ws.binaryType = "blob";
    wsRef.current = ws;

    ws.onopen = () => {
      reconnectAttempt.current = 0;

      ws.send(
        JSON.stringify({
          a: "subscribe",
          v: [[configRef.current.exchange, configRef.current.code]],
          m: "marketdata",
        })
      );

      heartbeatRef.current = setInterval(() => {
        if (ws.readyState === 1) {
          ws.send(
            JSON.stringify({
              a: "h",
              v: [[configRef.current.exchange, configRef.current.code]],
              m: "",
            })
          );
        }
      }, 10000);
    };

    ws.onmessage = (evt) => handleMessage(evt);

    ws.onclose = ws.onerror = () => {
      cleanup();
      scheduleReconnect();
    };
  };

  /* =========================
     Reconnect with backoff
  ========================= */
  const scheduleReconnect = () => {
    if (!isMarketOpen()) return;

    const delay = Math.min(
      1000 * Math.pow(2, reconnectAttempt.current),
      30000
    );

    reconnectAttempt.current += 1;

    reconnectRef.current = setTimeout(() => {
      connect();
    }, delay);
  };

  /* =========================
     Message handler
  ========================= */
  const handleMessage = async (response) => {
    if (!response?.data) return;

    const reader = new FileReader();
    reader.readAsArrayBuffer(response.data);

    reader.onloadend = () => {
      const data = new Uint8Array(reader.result);
      if (data.length < 86) return;

      const prevClose =
        stockDataRef.current?.close_price ?? -1;

      const parsed = readMarketData(data, prevClose);
      let livedata = parsed.livedata;

      /* ---- Duplicate tick guard ---- */
      const tickKey =
        livedata.last_traded_price + "_" + livedata.timestamp;

      if (lastTickRef.current === tickKey) return;
      lastTickRef.current = tickKey;

      /* ---- Market closed adjustment ---- */
      if (
        !isMarketOpen() &&
        livedata.last_traded_price === livedata.close_price
      ) {
        const lastClose =
          closeRef.current.find(
            (c) => c !== livedata.close_price
          ) ?? livedata.last_traded_price;

        const { change_price, change_percentage } =
          setChange(livedata.last_traded_price, lastClose);

        livedata.change_price = change_price;
        livedata.change_percentage = change_percentage;
      }

      stockDataRef.current = livedata;
      setStockData(livedata);
    };
  };

  /* =========================
     Effect: symbol change
  ========================= */
  useEffect(() => {
    cleanup();
    setStockData(null);
    stockDataRef.current = null;
    lastTickRef.current = null;

    if (!symbol) return;

    loadStaticData().then((ok) => {
      if (ok) connect();
    });

    return cleanup;
  }, [symbol, is_index]);

  /* =========================
     Unmount
  ========================= */
  useEffect(() => cleanup, []);

  return stockData;
};

export default useMarketStock;
